import { FC, useState } from 'react';
import { Trans } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { confirmCreditPack } from '../api';
import { useUserStateContext } from '../contexts/UserStateContext';
import { CircleLoading } from '../styles/Loadings';
import { FailureMessage, SuccessfulMessage } from '../styles/Messages';
import { useAsyncEffect } from '../utils';

const Container = styled.div`
  max-width: 520px;
  margin: 0px auto;
  padding: 60px 20px;
  color: #dcdcdc;
  text-align: center;
`;

const Title = styled.h1`
  font-size: 22px;
  font-weight: 700;
`;

const Description = styled.div`
  margin-top: 10px;
  font-size: 15px;
  color: #a8a8a8;
  line-height: 1.6;
`;

const PrimaryButton = styled.button`
  margin-top: 24px;
  height: 48px;
  padding: 0px 28px;
  border: none;
  border-radius: 24px;
  background-color: rgb(254, 167, 0);
  color: black;
  font-size: 16px;
  font-weight: 700;
  cursor: pointer;
`;

/**
 * クレジットパックの決済から戻ってくる先。
 * Webhook を持たない構成なので、ここで成立を確認してから付与する。
 */
const CreditPackPage: FC<{}> = () => {
  const { purchaseId } = useParams();
  const userState = useUserStateContext();
  const navigate = useNavigate();
  const [state, setState] = useState<"loading"|"granted"|"pending"|"failed">("loading");
  const [credits, setCredits] = useState(0);

  useAsyncEffect(async () => {
    if (!purchaseId || !userState.isSigned) {
      return;
    }
    try {
      const result = await confirmCreditPack(purchaseId);
      // already_granted は再読み込みなどの 2 回目以降。付与は済んでいるので成立扱い。
      if (result.status == "granted" || result.status == "already_granted") {
        setCredits(result.credits);
        setState("granted");
      } else if (result.actionUrl) {
        window.location.href = result.actionUrl;
      } else {
        setState("pending");
      }
    } catch (exception) {
      setState("failed");
    }
  }, [purchaseId, userState]);

  return <Container>
    {state == "loading" && <CircleLoading />}

    {state == "granted" && <>
      <SuccessfulMessage><Trans>Your videos are ready to generate</Trans></SuccessfulMessage>
      <Title>{credits} <Trans>videos added</Trans></Title>
      <Description><Trans>Pick a scene and it starts instantly, with no payment step.</Trans></Description>
      <PrimaryButton onClick={() => navigate("/scenes")}><Trans>Browse scenes</Trans></PrimaryButton>
    </>}

    {state == "pending" && <>
      <Title><Trans>Payment is still being processed</Trans></Title>
      <Description><Trans>Please check again in a few minutes.</Trans></Description>
    </>}

    {state == "failed" && <>
      <FailureMessage><Trans>Failed to confirm your purchase. Please try again.</Trans></FailureMessage>
      <PrimaryButton onClick={() => navigate("/scenes")}><Trans>Browse scenes</Trans></PrimaryButton>
    </>}
  </Container>;
};

export default CreditPackPage;
